import React, { useState } from 'react'
import styled from 'styled-components'
import { Text, theme } from '@alefeducation/ui-elements-library-core'
import { AngleRight, CaretDownSolid } from '@alefeducation/ui-elements-library-icons'
import { createPortal } from 'react-dom'
import { getFormattedDateInLocalTime } from '../../utils/dateUtils'
import AdditionalPanel from '../AdditionalPanel/AdditionalPanel'

export const LogsBlock = styled.div<{ $isFirstLog?: boolean }>`
  display: flex;
  padding-top: 24px;
  padding-left: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
  position: relative;
  &::after {
    content: ' ';
    position: absolute;
    left: 3.5px;
    top: 0;
    width: 1px;
    height: 100%;
    background-color: #d9d9d9;
  }
  &:first-of-type {
    padding-top: 0px;
    .panel-toggler-button {
      $isFirstLog?: undefined: top: 2px
    }
    &::after {
      top: 3.5px;
    }
  }
  &:first-of-type,
  &:last-of-type {
    &::before {
      content: ' ';
      position: absolute;
      width: 8px;
      height: 8px;
      left: 0px;
      top: 4px;
      background-color: #d9d9d9;
      border-radius: 100%;
    }
  }
  &:last-of-type {
    &::before {
      top: 28px;
    }
    &::after {
      height: 28px;
    }
  }
  &:only-of-type {
    &::before {
      top: 3.5px;
    }
    &::after {
      display: none;
    }
  }
  .panel-toggler-button {
    transform: rotate(0deg);
    position: absolute;
    right: 0;
    width: 10px;
    cursor: pointer;
  }
  .content {
    color: ${theme.colors.primary[100]};
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Signika;
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
  }
  .meta-info-wrapper {
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
  }
  .vertical-separator {
    width: 1px;
    height: 16px;
    display: block;
    background-color: ${theme.colors.primary[20]};
  }
  .entity-tag {
    display: flex;
    padding: 4px 8px;
    align-items: center;
    gap: 8px;
    border-radius: 9999px;
    background: ${theme.colors.primary[10]};
  }
  &:hover {
    .visible-on-hover {
      display: inherit !important;
    }
  }
`

const ChangesWrapper = styled.div`
  width: 100%;
  .changes-list {
    position: relative;
    height: 0px;
    padding: 0 0 0 10px;
    overflow: hidden;
    transition: max-height 500ms ease-in-out;
    > div {
      &:first-of-type {
        &::before {
          top: 7px;
        }
        &::after {
          top: 7px;
        }
      }
      &:last-of-type {
        &::before {
          top: 31px;
        }
        &::after {
          height: 31px;
        }
      }
      &:only-of-type::before {
        top: 5.5px;
      }
    }
  }
  &:has(.changes-checkbox:checked) {
    .changes-list {
      padding-top: 24px;
      height: auto;
      transition: max-height 500ms ease-in-out;
      margin-bottom: -24px;
      padding-bottom: 24px;
      overflow: visible;
      position: relative;
      &::before {
        content: ' ';
        position: absolute;
        background-color: ${theme.colors.primary[0]};
        height: 100%;
        width: 30px;
        top: 0;
        left: -28px;
        z-index: 2;
      }
    }
  }
  .label {
    display: flex;
    gap: 8px;
    align-items: center;
    cursor: pointer;
  }
  .changes-checkbox {
    display: none;
    &:checked {
      & + .label {
        svg {
          transform: rotate(0deg);
        }
      }
    }
  }
  svg {
    transform: rotate(270deg);
    transform-origin: center;
  }
`

const ChangesBlock = ({ changes, date }: { changes: AuditLogObject['fieldUpdateLog']; date: string }) => {
  const [showAdditionalPanel, setShowAdditionalPanel] = useState<{ show: boolean; change?: FieldUpdateLog }>({
    show: false,
    change: undefined
  })
  return (
    <ChangesWrapper>
      <label>
        <input type="checkbox" className="changes-checkbox" />
        {changes.length > 0 && (
          <div className="label">
            <CaretDownSolid width={10} height={10} />
            <Text variant="body-medium" weight="light" color="primary.100">
              {`Changes (${changes.length})`}
            </Text>
          </div>
        )}
      </label>
      <div className="changes-list">
        {changes.map(change => (
          <>
            <LogBlock
              auditDate=""
              fieldUpdateLog={null}
              key={date}
              message={change.message}
              customNode={
                (change.additionalDetails || change.fieldDisplayType === 'JSON') ? (
                  <AngleRight className="panel-toggler-button" onClick={() => setShowAdditionalPanel({ show: true, change: change })} />
                ) : null
              }
            />
          </>
        ))}
        {showAdditionalPanel.show &&
          (showAdditionalPanel.change.additionalDetails  || showAdditionalPanel.change.fieldDisplayType === 'JSON') &&
          createPortal(
            <AdditionalPanel
              {...showAdditionalPanel.change}
              auditDate={date}
              onClosePanel={() => setShowAdditionalPanel({ show: false, change: undefined })}
            />,
            document.body
          )}
      </div>
    </ChangesWrapper>
  )
}

export const DateBlock = ({ date }: { date: string }) => (
  <Text variant="body-medium" weight="light" color="primary.60">
    {getFormattedDateInLocalTime(date)}
  </Text>
)

const ReadMoreOrLessWrapper = styled.div`
  position: relative;
  label {
    height: 20px;
    display: block;
    color: ${theme.colors.interactive[120]};
    cursor: pointer;
    font-weight: 100;
  }
  &:has(.readMoreOrLess-checkbox:not(:checked)) {
    > .toggler {
      height: 40px;
      position: relative;
      overflow: hidden;
      word-break: break-all;
      > * {
        display: inline;
      }
      &::after {
        color: ${theme.colors.primary[100]};
        display: inline-block;
        content: '...';
        position: absolute;
        right: 0;
        bottom: -2px;
        z-index: 1;
        background-color: #fff;
      }
    }
    > label::after {
      content: 'Read more';
    }
  }
  &:has(.readMoreOrLess-checkbox:checked) {
    > .toggler {
      height: auto;
      word-break: break-word;
      position: relative;
      &::after {
        display: none;
      }
    }
    > label::after {
      content: 'Read less';
    }
  }
  .readMoreOrLess-checkbox {
    display: none;
  }
`

const ReadMoreOrLess = ({ content }: { content: string }) => {
  return (
    <ReadMoreOrLessWrapper>
      <div
        className="content toggler"
        dangerouslySetInnerHTML={{
          __html: content
        }}
      />
      <label className="label">
        <input type="checkbox" className="readMoreOrLess-checkbox" />
      </label>
    </ReadMoreOrLessWrapper>
  )
}

const LogBlock = (
  props: Pick<AuditLogObject, 'auditDate' | 'message' | 'fieldUpdateLog'> & {
    'data-testid'?: string
    customNode?: JSX.Element
    isFirstLog?: boolean
  }
) => {
  return (
    <LogsBlock data-testid={props['data-testid']} $isFirstLog={props.isFirstLog}>
      {props.auditDate.length ? <DateBlock date={props.auditDate} /> : null}
      {props.message.length < 100 ? (
        <div
          className="content"
          dangerouslySetInnerHTML={{
            __html: props.message
          }}
        />
      ) : (
        <ReadMoreOrLess content={props.message} />
      )}
      {Array.isArray(props.fieldUpdateLog) ? <ChangesBlock date={props.auditDate} changes={props.fieldUpdateLog} /> : null}
      {props.customNode || null}
    </LogsBlock>
  )
}

export default LogBlock
